import { motion } from "framer-motion";
export type Compartment = "skin" | "blood";
const EASE = [0.23, 1, 0.32, 1] as const;
export function SkinBloodIllustration({
  focus
}: {
  focus: Compartment | null;
}) {
  const skinOpacity = focus === "blood" ? 0.3 : 1;
  const bloodOpacity = focus === "skin" ? 0.3 : 1;
  return <svg viewBox="0 0 400 280" className="block h-auto w-full" role="img" aria-label="Schematic of skin layers with atypical T cells and a dermal blood vessel with circulating cells" data-id="element-367" data-mp="29cplrwvwq43f">
      <defs data-id="element-368" data-mp="1gotpgta92e76">
        <clipPath id="lumen-clip" data-id="element-369" data-mp="1gpd7e9zwh2l7">
          <path d={LUMEN} data-id="element-370" data-mp="1gq4surzs9jxr" />
        </clipPath>
      </defs>

      {/* SKIN COMPARTMENT */}
      <motion.g animate={{
      opacity: skinOpacity
    }} transition={{
      duration: 0.5,
      ease: EASE
    }} data-id="element-371" data-mp="1up4jg56uw8wk">
        <motion.rect x={0} y={56} width={400} height={224} fill="var(--color-tissue)" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 0.7
      }} data-id="element-372" data-mp="1qlyed0vhcnro" />
        {/* collagen fibres */}
        <motion.g stroke="#e4d2c6" strokeWidth={1} fill="none" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 1
      }} data-id="element-373" data-mp="1m5e48w172pf0">
          {[128, 146, 164, 258, 270].map((y, i) => <path key={y} d={`M${-10 + i * 12} ${y} C 80 ${y - 6}, 160 ${y + 6}, 240 ${y - 2} S 360 ${y + 4}, 410 ${y}`} data-id="element-374" data-mp="2066xzejo9gh2" data-mpi={i} />)}
        </motion.g>

        <motion.path d={EPIDERMIS} fill="#ecdcd2" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 0.9
      }} data-id="element-375" data-mp="13dv0uo39d9cg" />
        {/* keratinocytes */}
        <motion.g fill="none" stroke="#d9c2b4" strokeWidth={0.8} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 1.1
      }} data-id="element-376" data-mp="1h7mf29rxphei">
          {KERATINOCYTES.map(([x, y], i) => <ellipse key={i} cx={x} cy={y} rx={4.6} ry={3.2} data-id="element-377" data-mp="1s1pfsjwhj8mg" data-mpi={i} />)}
        </motion.g>

        {/* atypical T cells: Pautrier-type cluster, epidermotropism, dermal infiltrate */}
        <motion.g initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 1.4
      }} data-id="element-378" data-mp="1n0s0cf2v0son">
          {SKIN_T_CELLS.map(([x, y], i) => <g key={i} data-id="element-379" data-mp="1wtpc82dcjjea" data-mpi={i}>
              <TCell x={x} y={y} highlight={focus === "skin"} data-id="element-380" data-mp="1wt7fl4mrv35u" />
            </g>)}
        </motion.g>
      </motion.g>

      {/* Surface line — continues the navy timeline of the previous chapter, then settles as the skin surface */}
      <motion.path d={SURFACE} fill="none" strokeWidth={1.5} initial={{
      pathLength: 0,
      stroke: "#1e3a5f"
    }} animate={{
      pathLength: 1,
      stroke: "#b8998a"
    }} transition={{
      pathLength: {
        duration: 1.2,
        ease: EASE
      },
      stroke: {
        duration: 0.8,
        delay: 1.2
      }
    }} data-id="element-381" data-mp="1i419cwpiocog" />

      {/* BLOOD COMPARTMENT */}
      <motion.g initial={{
      opacity: 0
    }} animate={{
      opacity: bloodOpacity
    }} transition={{
      duration: 0.6,
      delay: focus === undefined ? 0 : 0,
      ease: EASE
    }} data-id="element-382" data-mp="2oj26h5qa30wm">
        <motion.g initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 1.5
      }} data-id="element-383" data-mp="312z5bg7d78ds">
          <path d={LUMEN} fill="var(--color-blood-soft)" data-id="element-384" data-mp="2n3zlclusrhny" />
          <g clipPath="url(#lumen-clip)" data-id="element-385" data-mp="2s0zrsxvk6mfl">
            <motion.g animate={{
            x: [-80, 0]
          }} transition={{
            duration: 5,
            ease: "linear",
            repeat: Infinity
          }} data-id="element-386" data-mp="2s1rm85df6e4a">
              {Array.from({
              length: 7
            }, (_, t) => <g key={t} transform={`translate(${t * 80 - 80} 0)`} data-id="element-387" data-mp="3ks9levb0vvcf">
                  {RBCS.map(([x, y, r], i) => <g key={i} transform={`rotate(${r} ${x} ${y})`} data-id="element-388" data-mp="3b0dlrftafx63" data-mpi={i}>
                      <ellipse cx={x} cy={y} rx={7} ry={4.4} fill="#d6a7a4" data-id="element-389" data-mp="3ayj2s71kuf4n" />
                      <ellipse cx={x} cy={y} rx={3.4} ry={1.8} fill="#e6c2bf" data-id="element-390" data-mp="2jsb7ul0cw0v" />
                    </g>)}
                  <TCell x={46} y={199} highlight={focus === "blood"} data-id="element-391" data-mp="7clhimw4kcf2" />
                </g>)}
            </motion.g>
          </g>
          <path d={WALL_TOP} fill="none" stroke="#c49593" strokeWidth={2} data-id="element-392" data-mp="2asqof7k4fypj" />
          <path d={WALL_BOTTOM} fill="none" stroke="#c49593" strokeWidth={2} data-id="element-393" data-mp="2avmj8n0oa79r" />
        </motion.g>
      </motion.g>

      {/* Labels */}
      <motion.g initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.6,
      delay: 2
    }} fontFamily="Inter, sans-serif" fontSize={11} fill="#353a42" data-id="element-394" data-mp="20vvs8vbbmaef">
        <text x={14} y={30} data-id="element-395" data-mp="2tl2qruaryzzo">Skin surface</text>
        <line x1={186} y1={34} x2={153} y2={70} stroke="#5f656e" strokeWidth={0.75} data-id="element-396" data-mp="2ot1ew9hirba6" />
        <text x={190} y={30} data-id="element-397" data-mp="2oqopxcdcg0nm">Atypical T cells in epidermis</text>
        <Label x={14} y={78} text="Epidermis" data-id="element-398" data-mp="2jtf2u3dfg12y" />
        <Label x={14} y={142} text="Dermis" data-id="element-399" data-mp="3h9c7iht4sxjy" />
        <Label x={14} y={262} text="Dermal blood vessel" data-id="element-400" data-mp="3ha40rlz9aj72" />
      </motion.g>
    </svg>;
}
function Label({
  x,
  y,
  text
}: {
  x: number;
  y: number;
  text: string;
}) {
  return <text x={x} y={y} stroke="#ffffff" strokeWidth={3.5} paintOrder="stroke" strokeLinejoin="round" data-id="element-401" data-mp="3jujwnmsh6qvy">
      {text}
    </text>;
}
function TCell({
  x,
  y,
  highlight
}: {
  x: number;
  y: number;
  highlight: boolean;
}) {
  return <g data-id="element-402" data-mp="3tqd62xawe49i">
      <circle cx={x} cy={y} r={5.2} fill="#ffffff" stroke={highlight ? "#2a6f73" : "#8c99aa"} strokeWidth={highlight ? 1.4 : 0.9} data-id="element-403" data-mp="3tqmw2kvy0bf7" />
      <path d={`M${x - 3.2} ${y} c 0.8 -2.6, 2 -0.6, 2.4 -2.6 c 0.6 1.6, 2.4 0.2, 2.4 1.8 c 0.2 1.6, 1.6 1.2, 0.8 3 c -1.2 0.6, -1.8 -0.8, -2.8 0.8 c -1 -1, -2.4 0.2, -2.8 -3 z`} fill={highlight ? "#2a6f73" : "#1e3a5f"} opacity={0.82} data-id="element-404" data-mp="6n8cud8nf2qr" />
    </g>;
}

// ---- geometry ----

const SURFACE = "M0 56 C 50 50, 90 62, 140 55 S 230 49, 280 57 S 360 52, 400 56";
function buildEpidermis() {
  let d = SURFACE + " L400 88";
  for (let x = 400; x > 0; x -= 40) {
    d += ` C ${x - 8} 88, ${x - 12} 108, ${x - 20} 108 C ${x - 28} 108, ${x - 32} 88, ${x - 40} 88`;
  }
  return d + " Z";
}
const EPIDERMIS = buildEpidermis();
const KERATINOCYTES: [number, number][] = (() => {
  const out: [number, number][] = [];
  for (let row = 0; row < 3; row++) {
    for (let x = 8 + row % 2 * 7; x < 396; x += 14) {
      const y = 66 + row * 8 + Math.sin(x / 23) * 1.5;
      if (Math.abs(x - 150) < 16 && row > 0) continue; // space for the cluster
      out.push([x, y]);
    }
  }
  return out;
})();
const SKIN_T_CELLS: [number, number][] = [[146, 76], [155, 73], [151, 82], [160, 81], [232, 70], [304, 78], [60, 120], [84, 128], [112, 118], [138, 130], [176, 122], [204, 132], [236, 120], [270, 128], [322, 121], [356, 131]];
const WALL_TOP = "M0 188 C 100 182, 200 194, 300 186 S 380 184, 400 188";
const WALL_BOTTOM = "M0 234 C 100 228, 200 240, 300 232 S 380 230, 400 234";
const LUMEN = "M0 188 C 100 182, 200 194, 300 186 S 380 184, 400 188 L400 234 C 380 230, 340 230, 300 232 C 200 240, 100 228, 0 234 Z";
const RBCS: [number, number, number][] = [[10, 204, -10], [28, 222, 12], [62, 212, -6], [72, 196, 18], [16, 190, 4]];